import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { healthApi } from '@/lib/health-api';
import { notificationsApi, type Notification } from '@/services/api';
import {
  LayoutDashboard, Server, Video, Ticket, Building2, FileText, LogOut,
  ChevronRight, ChevronDown, Search, Bell, Boxes, MapPin, Users, BarChart3, FileBarChart, Settings as SettingsIcon,
  Map as MapIcon, Network, Siren, HardDriveDownload, Briefcase, Box,
  Gauge, ArrowUpCircle, Home, Radio, Headset, Maximize, Minimize,
  Clapperboard,
  Check, CheckCheck, Globe, X, Zap, AlertTriangle, WifiOff,
  PanelLeft, PanelLeftClose,
} from 'lucide-react';
import { useAuth, isAdminUser } from '@/auth/AuthContext';
import { LiveConnection } from '@/live/LiveConnection';
import { IOSentraLogo } from '@/components/IOSentraLogo';
import { IOSentraWordmark } from '@/components/IOSentraWordmark';
import { userPrefsApi, subscriptionApi } from '@/lib/inventory-api';
import { useUIStore } from '@/stores/ui.store';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

import type { Plan } from '@/auth/AuthContext';

// Flat sidebar: one direct entry per top-level destination (no group headers).
// Multi-page destinations (Assurance, Inventory, Service Desk, Insights,
// Configuration) land on their first page and expose the rest as an in-page tab
// strip (SectionTabs). `match` (optional) lists the extra route prefixes that
// should highlight this entry as active (the section's sub-pages).
interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  plan?: Plan | 'shared';
  adminOnly?: boolean;
  /** Extra route prefixes (besides `to`) that mark this entry active. */
  match?: string[];
}

// Routes that actually have a page today. The sidebar models the full target
// IA, but items whose route isn't live are filtered out so nav never
// dead-links. As pages ship, just add the route here (and in App.tsx).
const LIVE_ROUTES = new Set<string>([
  '/', '/cameras', '/edges', '/map', '/topology', '/ai-health', '/recording',
  '/issues', '/tickets', '/sla', '/escalations', '/vendor-performance',
  '/devices', '/firmware', '/sites', '/vendors', '/contracts', '/assets', '/team',
  '/analytics', '/reports', '/provider', '/executive', '/settings', '/automation',
  // Video Assurance (§7.8–7.9) — /noc is chromeless so it's intentionally absent.
  '/video-assurance', '/video-assurance/cameras', '/video-assurance/recording-verification',
  '/video-assurance/blur', '/video-assurance/retention',
  '/video-assurance/playback',
]);

// Flat operator-first IA: 9 direct entries, no collapsible groups. Multi-page
// destinations (Assurance/Inventory/Service Desk/Insights/Configuration) land on
// their first page and expose the rest as an in-page tab strip (SectionTabs), so
// the sidebar stays a single flat list. Plan/admin gating is per-entry.
const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboards', icon: LayoutDashboard, end: true },
  { to: '/cameras', label: 'Live Video', icon: Video },
  { to: '/map', label: 'Maps', icon: MapIcon },
  { to: '/issues', label: 'Issues', icon: AlertTriangle },
  { to: '/video-assurance', label: 'Assurance', icon: Clapperboard, plan: 'health-monitoring',
    match: ['/recording', '/ai-health'] },
  { to: '/devices', label: 'Inventory', icon: Boxes,
    match: ['/sites', '/assets', '/topology', '/firmware'] },
  { to: '/tickets', label: 'Service Desk', icon: Headset, plan: 'service-desk',
    match: ['/sla', '/escalations', '/vendors', '/vendor-performance', '/contracts'] },
  { to: '/analytics', label: 'Insights', icon: BarChart3, match: ['/reports'] },
  { to: '/edges', label: 'Configuration', icon: SettingsIcon, adminOnly: true,
    match: ['/automation', '/team', '/settings'] },
];

// Drop entries whose landing route isn't live (defensive — all 9 are live today).
function liveItems(items: NavItem[]): NavItem[] {
  return items.filter((it) => LIVE_ROUTES.has(it.to));
}

/** True when `it` owns the current path (its landing route or any matched prefix). */
function itemActive(it: NavItem, pathname: string): boolean {
  if (it.end ? pathname === it.to : pathname === it.to || pathname.startsWith(it.to + '/')) return true;
  return (it.match ?? []).some((m) => pathname === m || pathname.startsWith(m + '/'));
}

const NAV = NAV_ITEMS;

// Plan-limit banner — shown when this tenant is at/over a subscription cap, so a
// tenant admin knows to upgrade (new cameras/devices/users are blocked at the API).
function PlanLimitBanner() {
  const { t } = useTranslation('nav');
  const { data } = useQuery({ queryKey: ['subscription'], queryFn: subscriptionApi.get, retry: false, refetchInterval: 120_000 });
  if (!data || data.overLimit.length === 0) return null;
  const items = data.overLimit.map((k) => `${data.usage[k]}/${data.limits[k]} ${k}`).join(' · ');
  return (
    <div className="mb-4 flex items-center gap-2 rounded-md bg-warning/10 px-4 py-2.5 text-sm text-warning ring-1 ring-warning/30">
      <Zap size={15} className="shrink-0" />
      <span>{t('planLimit', { items, kinds: data.overLimit.join(' / ') })}</span>
    </div>
  );
}

// Offline banner (audit UI-02) — shown when the browser loses connectivity so a
// failed request reads as "you're offline" rather than a raw fetch error.
function OfflineBanner() {
  const { t } = useTranslation('common');
  const online = useOnlineStatus();
  if (online) return null;
  return (
    <div className="mb-4 flex items-center gap-2 rounded-md bg-critical/10 px-4 py-2.5 text-sm text-critical ring-1 ring-critical/30">
      <WifiOff size={15} className="shrink-0" />
      <span>{t('offline.banner')}</span>
    </div>
  );
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { user, logout, hasPlan } = useAuth();
  const { pathname } = useLocation();
  const { t } = useTranslation(['nav', 'common']);

  // Visible entries = live + entitled by plan + (for admin-only entries) the user
  // holds an admin role. A viewer no longer sees Configuration. Same isAdminUser()
  // the route guard uses (RequireAdmin) — one source of truth.
  const isAdmin = isAdminUser(user);
  const items = useMemo(
    () => liveItems(NAV_ITEMS)
      .filter((it) => !it.plan || it.plan === 'shared' || hasPlan(it.plan))
      .filter((it) => !it.adminOnly || isAdmin),
    [hasPlan, isAdmin],
  );

  // Auto-hide: the sidebar collapses to an icon rail and expands on hover.
  // A fixed-width spacer keeps page content from reflowing; the aside itself
  // is absolutely positioned so the expanded panel overlays the content.
  const [hovered, setHovered] = useState(false);
  const pinned = useUIStore((s) => s.sidebarPinned);
  const togglePinned = useUIStore((s) => s.toggleSidebarPinned);
  // Pinned keeps it open (works without a mouse / on touch); otherwise hover.
  const expanded = pinned || hovered;

  // Keyboard toggle for the pin (ignore when typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (!typing && e.key === '[' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        togglePinned();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [togglePinned]);

  return (
    <div className="relative flex h-full">
      {/* Live channel — one tenant-scoped /ws/live socket for the session;
          delivers automation operator-alerts (toast/sound/voice). Renders nothing. */}
      <LiveConnection />
      {/* Rail spacer — reserves the collapsed width so content doesn't shift */}
      <div className="w-16 shrink-0" aria-hidden />

      {/* Sidebar — glass chrome; icon rail that expands to full width on hover */}
      <aside
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`tk-chrome absolute inset-y-0 start-0 z-30 flex flex-col border-e transition-[width] duration-200 ease-out ${
          expanded ? 'w-64 shadow-2xl shadow-black/40' : 'w-16'
        }`}
      >
        <div className={`flex items-center gap-2 py-5 text-lg font-semibold tracking-tight ${expanded ? 'px-5' : 'justify-center px-0'}`}>
          <IOSentraLogo size={28} className="shrink-0" />
          {expanded && (
            <>
              <IOSentraWordmark className="flex-1 truncate" />
              {/* Pin keeps the sidebar open (not hover-only). Persisted; toggle with [. */}
              <button
                type="button"
                onClick={togglePinned}
                aria-pressed={pinned}
                title={`${pinned ? t('sidebar.unpin', 'Unpin sidebar') : t('sidebar.pin', 'Pin sidebar open')} ([)`}
                aria-label={pinned ? t('sidebar.unpin', 'Unpin sidebar') : t('sidebar.pin', 'Pin sidebar open')}
                className={`grid h-7 w-7 place-items-center rounded-md transition-colors hover:bg-white/10 ${pinned ? 'text-primary' : 'text-muted'}`}
              >
                {pinned ? <PanelLeftClose size={16} /> : <PanelLeft size={16} />}
              </button>
            </>
          )}
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-3 pb-4">
          {items.map(({ to, label, icon: Icon, match }) => {
            // Active if this entry owns the path (its own route or a section
            // sub-route). Computed here (not NavLink's isActive) so a section's
            // tabbed sub-pages keep the parent entry highlighted.
            const active = itemActive({ to, label, icon: Icon, match }, pathname);
            return (
              <NavLink
                key={to}
                to={to}
                title={!expanded ? t(`items.${label}`, label) : undefined}
                onClick={() => setHovered(false)}
                className={`group relative flex items-center rounded-lg text-sm transition-colors ${
                  expanded ? 'gap-3 px-3 py-2' : 'justify-center px-0 py-2.5'
                } ${active ? 'bg-white/10 text-fg shadow-inner' : 'text-muted hover:text-fg hover:bg-white/5'}`}
              >
                {active && expanded && (
                  <span className="absolute start-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-gradient-to-b from-primary to-accent" />
                )}
                <Icon size={expanded ? 16 : 18} className={active ? 'text-primary' : ''} />
                {expanded && t(`items.${label}`, label)}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3 text-xs text-muted">
          {expanded ? (
            <>
              <div className="truncate">{user?.name}</div>
              <div className="truncate">{user?.email}</div>
              <button onClick={() => logout()} className="tk-btn-ghost mt-2 w-full justify-start px-2">
                <LogOut size={16} /> {t('common:actions.signOut')}
              </button>
            </>
          ) : (
            <button onClick={() => logout()} title={t('common:actions.signOut')} className="tk-btn-ghost flex w-full justify-center px-0">
              <LogOut size={16} />
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopBar />
        <main className="flex-1 overflow-auto">
          {/* Full-width content with comfortable side padding (no narrow cap)
              so pages use the available space on wide screens. */}
          <div className="w-full px-6 py-6 2xl:px-10">
            <OfflineBanner />
            <PlanLimitBanner />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

// Toggle the browser's native fullscreen, tracking the current state so the
// button can swap its icon. Stays in sync if the user exits via Esc / F11.
function useFullscreen() {
  const [isFull, setIsFull] = useState(() => typeof document !== 'undefined' && !!document.fullscreenElement);
  useEffect(() => {
    const onChange = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggle = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
    } else {
      void document.documentElement.requestFullscreen?.();
    }
  };
  return { isFull, toggle };
}

// Customer (tenant) switcher — one identity belongs to many customers, each
// with its own role. Lists the user's memberships and re-issues the token
// (switch-tenant) for the chosen customer. Hidden when the user has only one.
function CustomerSwitcher() {
  const { user, switchTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const memberships = user?.memberships ?? [];
  if (memberships.length < 2) return null;

  const active = memberships.find((m) => m.tenantId === user?.activeTenantId) ?? memberships[0];

  return (
    <div className="relative">
      <button
        className="tk-btn-ghost flex items-center gap-2 px-2 text-sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Switch customer"
      >
        <Building2 size={16} className="text-muted" />
        <span className="max-w-[12rem] truncate font-medium">{active.name}</span>
        <ChevronDown size={14} className={`text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <ul
            role="listbox"
            className="tk-modal absolute end-0 z-40 mt-1 max-h-80 w-64 overflow-auto p-1"
          >
            {memberships.map((m) => {
              const isActive = m.tenantId === active.tenantId;
              return (
                <li key={m.tenantId}>
                  <button
                    role="option"
                    aria-selected={isActive}
                    disabled={isActive}
                    onClick={() => { setOpen(false); if (!isActive) void switchTenant(m.tenantId); }}
                    className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-white/5 ${isActive ? 'opacity-100' : 'opacity-90'}`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{m.name}</span>
                      <span className="block truncate text-xs text-muted">{m.roles.join(', ')}</span>
                    </span>
                    {isActive && <Check size={16} className="shrink-0 text-primary" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

function TopBar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { t } = useTranslation(['nav', 'common']);
  const { isFull, toggle: toggleFull } = useFullscreen();
  // Flat nav → breadcrumb is just the current top-level section.
  const currentItem = NAV.find((n) => itemActive(n, pathname))?.label ?? 'Dashboards';
  return (
    <header className="tk-chrome relative z-40 flex h-14 items-center justify-between border-b px-6">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted">{t('common:appName')}</span>
        <ChevronRight size={14} className="text-muted rtl:rotate-180" />
        <span className="font-medium">{t(`items.${currentItem}`, currentItem)}</span>
      </div>
      <div className="flex items-center gap-2">
        <CustomerSwitcher />
        <CommandPalette />
        <button
          className="tk-btn-ghost px-2"
          onClick={toggleFull}
          aria-label={isFull ? t('topbar.exitFullscreen') : t('topbar.fullscreen')}
          title={isFull ? t('topbar.exitFullscreen') : t('topbar.fullscreen')}
        >
          {isFull ? <Minimize size={18} /> : <Maximize size={18} />}
        </button>
        <NotificationsMenu />
        <UserMenu />
      </div>
    </header>
  );
}

/** Click-away helper: calls onClose when a click lands outside the ref'd node. */
function useClickAway<T extends HTMLElement>(onClose: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  return ref;
}

/** Search → a keyboard-driven command palette that jumps to any live page.
 *  Opens on click or Ctrl/Cmd-K. */
function CommandPalette() {
  const navigate = useNavigate();
  const { hasPlan, user } = useAuth();
  const { t } = useTranslation('nav');
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Only pages the user's plan can see, and only live routes (NAV is already
  // filtered to live routes via LIVE_ROUTES).
  const isAdmin = isAdminUser(user);
  const pages = useMemo(() => {
    return liveItems(NAV_ITEMS)
      .filter((it) => !it.plan || it.plan === 'shared' || hasPlan(it.plan))
      .filter((it) => !it.adminOnly || isAdmin);
  }, [hasPlan, isAdmin]);
  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = t ? pages.filter((p) => p.label.toLowerCase().includes(t)) : pages;
    return list.slice(0, 8);
  }, [pages, q]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(true); }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 0); else setQ(''); }, [open]);

  const go = (to: string) => { setOpen(false); navigate(to); };

  return (
    <>
      <button className="tk-btn-ghost px-2" aria-label={t('topbar.search')} title={`${t('topbar.search')} (Ctrl/⌘ K)`} onClick={() => setOpen(true)}><Search size={18} /></button>
      {open && (
        <div className="fixed inset-0 z-[60] grid place-items-start justify-center bg-black/40 p-4 pt-[12vh]" onClick={() => setOpen(false)}>
          <div className="tk-modal w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
              <Search size={16} className="text-muted" />
              <input
                ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={t('topbar.searchPlaceholder')}
                className="w-full bg-transparent text-sm text-fg outline-none placeholder:text-muted"
                onKeyDown={(e) => { if (e.key === 'Enter' && results[0]) go(results[0].to); }}
              />
              <kbd className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-muted ring-1 ring-white/10">ESC</kbd>
            </div>
            <ul className="max-h-80 overflow-auto p-1">
              {results.length === 0 ? (
                <li className="px-3 py-6 text-center text-sm text-muted">{t('topbar.noMatch')}</li>
              ) : results.map((p) => {
                const Icon = p.icon;
                return (
                  <li key={p.to}>
                    <button onClick={() => go(p.to)} className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-white/5">
                      <Icon size={16} className="shrink-0 text-muted" />
                      <span className="font-medium">{t(`items.${p.label}`, p.label)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

/** Notifications → recent open incidents; the red dot reflects the real count. */
// Icon tint per notification severity (matches the feed's visual vocabulary).
function severityTone(sev: Notification['severity']): string {
  switch (sev) {
    case 'critical': return 'text-critical';
    case 'warning': return 'text-warning';
    case 'success': return 'text-ok';
    default: return 'text-muted';
  }
}

function NotificationsMenu() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation(['nav', 'common']);
  const [open, setOpen] = useState(false);
  const ref = useClickAway<HTMLDivElement>(() => setOpen(false));

  // Badge: cheap unread count on a 30s poll. Feed: recent notifications, only
  // fetched while the menu is open (no need to poll the full list in the bg).
  const { data: unread } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 30_000,
    retry: false,
  });
  const { data: feed } = useQuery({
    queryKey: ['notifications', 'feed'],
    queryFn: () => notificationsApi.list({ limit: 8 }),
    enabled: open,
    retry: false,
  });

  const unreadCount = unread?.unreadCount ?? 0;
  const items = feed?.items ?? [];

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  };

  const openItem = async (n: Notification) => {
    setOpen(false);
    if (!n.readAt) {
      try { await notificationsApi.markRead(n.id); refresh(); } catch { /* best-effort */ }
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await notificationsApi.markAllRead(); refresh(); } catch { /* best-effort */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button className="relative tk-btn-ghost px-2" aria-label={t('topbar.notifications')} onClick={() => setOpen((v) => !v)}>
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute end-0.5 top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-critical px-1 text-[9px] font-semibold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="tk-modal absolute end-0 z-50 mt-1 w-80 overflow-hidden p-0">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5 text-sm font-medium">
            <span>{t('topbar.notifications')}</span>
            {unreadCount > 0 && (
              <button onClick={markAll} className="flex items-center gap-1 text-xs text-primary hover:underline">
                <CheckCheck size={13} /> {t('topbar.markAllRead')}
              </button>
            )}
          </div>
          <ul className="max-h-80 overflow-auto">
            {items.length === 0 ? (
              <li className="px-4 py-8 text-center text-sm text-muted">{t('topbar.noNotifications')}</li>
            ) : items.map((n) => (
              <li key={n.id}>
                <button
                  onClick={() => void openItem(n)}
                  className={`flex w-full items-start gap-2.5 border-b border-white/5 px-4 py-2.5 text-left last:border-0 hover:bg-white/5 ${n.readAt ? '' : 'bg-white/[0.03]'}`}
                >
                  <Siren size={14} className={`mt-0.5 shrink-0 ${severityTone(n.severity)}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{n.title}</span>
                    {n.body && <span className="block truncate text-xs text-muted">{n.body}</span>}
                    <span className="block truncate text-[11px] text-muted">{fmtAgo(n.createdAt)}</span>
                  </span>
                  {!n.readAt && <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />}
                </button>
              </li>
            ))}
          </ul>
          <button onClick={() => { setOpen(false); navigate('/issues'); }} className="w-full border-t border-white/10 px-4 py-2.5 text-center text-xs text-primary hover:bg-white/5">
            {t('topbar.viewAllIncidents')}
          </button>
        </div>
      )}
    </div>
  );
}

/** User avatar → a menu with identity, tenant switch hint, and sign out. */
function UserMenu() {
  const { user, logout } = useAuth();
  const { t } = useTranslation(['nav', 'common']);
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const ref = useClickAway<HTMLDivElement>(() => setOpen(false));
  const memberships = user?.memberships ?? [];
  const active = memberships.find((m) => m.tenantId === user?.activeTenantId);
  return (
    <div className="relative ml-1" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)} aria-label={t('topbar.accountMenu')}
        className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-primary to-accent/70 text-xs font-semibold text-white ring-2 ring-transparent hover:ring-white/20"
      >
        {(user?.name ?? 'U').slice(0, 1).toUpperCase()}
      </button>
      {open && (
        <div className="tk-modal absolute end-0 z-50 mt-1 w-64 overflow-hidden p-0">
          <div className="border-b border-white/10 px-4 py-3">
            <div className="truncate text-sm font-medium">{user?.name ?? 'User'}</div>
            <div className="truncate text-xs text-muted">{user?.email}</div>
            {active && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-muted">
                <Building2 size={12} /> {active.name}
                {active.roles?.length ? <span className="rounded bg-white/5 px-1.5 py-0.5 text-[11px] ring-1 ring-white/10">{active.roles[0]}</span> : null}
              </div>
            )}
          </div>
          {memberships.length > 1 && (
            <div className="border-b border-white/10 px-4 py-2 text-xs text-muted">Use the customer switcher (top-left of this bar) to change organization.</div>
          )}
          <button onClick={() => { setOpen(false); setProfileOpen(true); }} className="flex w-full items-center gap-2 border-b border-white/10 px-4 py-2.5 text-left text-sm hover:bg-white/5">
            <Globe size={15} className="text-muted" /> {t('topbar.profileLanguage')}
          </button>
          <button onClick={() => { setOpen(false); logout(); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-white/5">
            <LogOut size={15} className="text-muted" /> {t('common:actions.signOut')}
          </button>
        </div>
      )}
      {profileOpen && <ProfileSettings user={user} onClose={() => setProfileOpen(false)} />}
    </div>
  );
}

// Profile settings — identity + language preference (persisted per user). Only
// English is available now; the dropdown lists languages we can add as the app
// gets translated. English + Hebrew (RTL) are live; the others are listed as
// coming-soon until their locale files land.
// Per-user notification channels (these are MY delivery prefs, not the tenant's
// recipient config — which is why they live in the profile, reachable by any user).
const NOTIF_CHANNELS = ['Email', 'Mobile push', 'Slack', 'SMS', 'Webhook'] as const;

function ProfileSettings({ user, onClose }: { user: ReturnType<typeof useAuth>['user']; onClose: () => void }) {
  const { t } = useTranslation(['profile', 'common']);
  const qc = useQueryClient();
  const { data: prefs } = useQuery({ queryKey: ['user-prefs'], queryFn: userPrefsApi.get, retry: false });
  const storeLanguage = useUIStore((s) => s.language);
  const storeSetLanguage = useUIStore((s) => s.setLanguage);
  const [language, setLanguage] = useState<string>(storeLanguage);
  const [saved, setSaved] = useState(false);
  const [notif, setNotif] = useState<Record<string, boolean>>({ Email: true, 'Mobile push': true });
  useEffect(() => { if (prefs?.language) setLanguage(prefs.language); }, [prefs]);
  useEffect(() => { if (prefs?.notificationChannels) setNotif(prefs.notificationChannels); }, [prefs]);
  const toggleNotif = async (c: string) => {
    const next = { ...notif, [c]: !notif[c] };
    setNotif(next);
    await userPrefsApi.setNotificationChannels(next);
    qc.invalidateQueries({ queryKey: ['user-prefs'] });
  };
  const LANGS: Array<{ code: string; label: string; enabled: boolean }> = [
    { code: 'en', label: 'English', enabled: true },
    { code: 'he', label: 'עברית (Hebrew)', enabled: true },
    { code: 'ar', label: 'العربية (Arabic)', enabled: false },
    { code: 'es', label: 'Español (Spanish)', enabled: false },
  ];
  // Switch live (store handles localStorage + i18n.changeLanguage + <html dir>
  // + the debounced API sync), then surface a brief "saved".
  const save = () => {
    storeSetLanguage(language as typeof storeLanguage);
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };
  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <div className="tk-modal w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('title')}</h2>
          <button className="tk-btn-ghost px-2" onClick={onClose} aria-label={t('common:actions.close')}><X size={16} /></button>
        </div>
        <dl className="mb-4 space-y-1 text-sm text-muted">
          <div className="flex justify-between"><dt>{t('name')}</dt><dd className="text-fg">{user?.name ?? '—'}</dd></div>
          <div className="flex justify-between"><dt>{t('email')}</dt><dd className="text-fg">{user?.email ?? '—'}</dd></div>
        </dl>
        <label className="block text-xs text-muted">{t('language')}
          <select className="tk-input mt-1 w-full py-1.5 text-sm" value={language} onChange={(e) => setLanguage(e.target.value)}>
            {LANGS.map((l) => <option key={l.code} value={l.code} disabled={!l.enabled}>{l.label}{l.enabled ? '' : ` — ${t('comingSoon')}`}</option>)}
          </select>
        </label>
        <p className="mt-1 text-[11px] text-muted">{t('languageHint')}</p>

        {/* My notification channels — per-user delivery prefs (moved here from
            the admin-gated Settings page; these are personal, not tenant config). */}
        <div className="mt-4 border-t border-white/10 pt-4">
          <div className="mb-2 text-sm font-medium text-fg">{t('notifications.heading', 'My notifications')}</div>
          <div className="space-y-2">
            {NOTIF_CHANNELS.map((c) => (
              <div key={c} className="flex items-center justify-between text-sm text-muted">
                {t(`notifications.channels.${c}`, { defaultValue: c })}
                <button
                  type="button" role="switch" aria-checked={!!notif[c]} onClick={() => toggleNotif(c)}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${notif[c] ? 'bg-primary' : 'bg-white/10 ring-1 ring-white/15'}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${notif[c] ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted">{t('notifications.footer', 'Choose how you personally receive alerts. Mobile push requires the IOSentra app.')}</p>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          {saved && <span className="text-xs text-ok">{t('saved')}</span>}
          <button className="tk-btn-ghost" onClick={onClose}>{t('common:actions.close')}</button>
          <button className="tk-btn-primary" onClick={save}>{t('common:actions.save')}</button>
        </div>
      </div>
    </div>
  );
}

/** Compact relative-time for the notifications list. */
function fmtAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
